/**
 * SceneNode - Thin descriptor consumer for Three.js scene construction.
 *
 * Uses buildSceneDescriptor() to get a pure data model of the scene,
 * then realizes it into Three.js objects. Distributes object refs back
 * to declaration nodes for self-mutation on param change.
 *
 * Flow: upstream nodes (declaration) → buildSceneDescriptor → SceneNode.realize() → Camera
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import { rgbaToHex, type RGBA } from '../colorUtils';
import { buildSceneDescriptor } from '../buildSceneDescriptor';
import { realizeScene } from '../realizeScene';
import { logger } from '@/logging';

const sceneEndpoints = {
  ...internals({
    meshIn: { dataType: 'three:group', isInput: true, label: 'Meshes' },
    lightIn: { dataType: 'three:light', isInput: true, label: 'Lights' },
    sceneOut: { dataType: 'three:scene', isOutput: true }
  }),
  ...params({
    background: { dataType: 'color', value: { r: 0, g: 0, b: 0, a: 1 } }
  })
};

class SceneNode extends BaseNode<typeof sceneEndpoints> {
  private three: Three;
  private scene: any = null;

  /** Version tracking for topology changes */
  private lastGraphVersion = -1;

  /** Base ambient light (fallback when no lights connected) */
  private baseAmbient: any = null;

  /** All Three.js objects added to scene (for cleanup) */
  private sceneObjects: any[] = [];

  /** Per-frame callbacks from upstream nodes with function params */
  private storedAnimateCallbacks: (() => void)[] = [];

  /** Whether a rebuild is requested (by upstream node data change) */
  private rebuildRequested = false;

  /** Flag for CameraNode's findUpstream to detect */
  needsPerFrameUpdate = true;

  /** Per-frame: check graph version, re-realize if needed, run upstream animates */
  animate(): void {
    const graphIndex = this.deps.graphIndex;
    if (graphIndex.version !== this.lastGraphVersion || this.rebuildRequested) {
      this.realize();
      this.lastGraphVersion = graphIndex.version;
      this.rebuildRequested = false;
    }
    // Run animate on upstream nodes with function params (useExp)
    for (const cb of this.storedAnimateCallbacks) {
      cb();
    }
    // Update own params if any have propagated get functions (e.g., animated background)
    if (this.hasFunctionParams()) {
      this.updateBackground();
    }
  }

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  initialize(): void {
    super.initialize();
    if (this.three) {
      this.scene = new this.three.Scene();
      this.updateBackground();
      this.baseAmbient = new this.three.AmbientLight(0xffffff, 0.3);
      this.scene.add(this.baseAmbient);
      this.internals.sceneOut.set(this.scene);
    }
  }

  private updateBackground(): void {
    if (this.scene && this.three) {
      const color = this.params.background.value as RGBA;
      this.scene.background = new this.three.Color(rgbaToHex(color));
    }
  }

  // ─── Realize ──────────────────────────────────────────────

  private realize(): void {
    const _rt0 = performance.now();
    const descriptor = buildSceneDescriptor(
      this.deps.graphIndex, this.id, this.deps.instancePath
    );
    const _rt1 = performance.now();
    this.clearScene();

    const requestRebuild = () => { this.rebuildRequested = true; };
    const result = realizeScene(descriptor, this.scene, this.three, requestRebuild);
    const _rt2 = performance.now();
    this.sceneObjects = result.objects;
    this.storedAnimateCallbacks = result.animateCallbacks;
    logger.info('SceneNode', `realize: buildDescriptor=${(_rt1-_rt0).toFixed(1)}ms realizeScene=${(_rt2-_rt1).toFixed(1)}ms total=${(_rt2-_rt0).toFixed(1)}ms meshes=${descriptor.meshes.length} lights=${descriptor.lights.length}`);
  }

  private clearScene(): void {
    if (!this.scene) return;

    for (const obj of this.sceneObjects) {
      this.scene.remove(obj);
      if (obj.target) this.scene.remove(obj.target);
    }
    this.sceneObjects = [];
  }

  // ─── Param handling ────────────────────────────────────────

  onParamChanged(_payload: any, endpointId: string): void {
    if (endpointId === 'background') {
      this.updateBackground();
    }
  }

  getScene(): any {
    return this.scene;
  }

  dispose(): void {
    this.clearScene();
    this.scene = null;
    super.dispose();
  }
}

export const sceneNodeDef: NodeDef<SceneNode> = {
  type: 'Scene',
  shortName: 'scene',
  info: 'Realizes upstream geometry declarations into a Three.js scene with camera and lighting.',
  category: 'scene',
  endpoints: sceneEndpoints,
  ctor: SceneNode
};
